import { DAEMON_URL } from "../config";

export type QaIssueCategory = "visual" | "static" | "functional" | "runtime";
export type QaIssueSeverity = "high" | "medium" | "low";

/** One CURRENT open QA defect for a pack. Mirrors the qa-agent PublicIssue shape
 *  (the daemon proxies it through /projects/:project/qa-issues). */
export interface QaIssue {
  repo: string;
  jobId: number;
  buildSha: string;
  category: QaIssueCategory;
  severity: QaIssueSeverity;
  title: string;
  detail: string;
  contentId?: string;
  /** Stable id the dashboard uses to address this issue (mark-fixed / cancel). */
  signature: string;
  /** Screenshot basenames + absolute URLs the browser can <img>. */
  shots: string[];
  shotUrls: string[];
  /** Flagged mid-run, before the final synthesis confirmed it. */
  provisional?: boolean;
  /** Developer acknowledgement of this issue (the "mark as fixed" flow).
   *  - "fixed": a dev marked it fixed.
   *  - "disputed": it was fixed but Emil re-detected it on a later run. */
  ack?: { status: "fixed" | "disputed"; devName: string; fixedAt: string; disputedAt?: string };
  firstSeenAt: string;
  lastSeenAt: string;
  /** Present only for resolved (Fixed-ledger) issues: when it was fixed + why. */
  resolvedAt?: string;
  resolution?: "verified";
}

export interface QaIssuesResponse {
  issues: QaIssue[];
  count: number;
  /** Fixed-ledger issues (verified clean), newest first. Absent on older daemons. */
  resolved?: QaIssue[];
  resolvedCount?: number;
  /** False when the daemon has no qa-agent URL/token configured. */
  configured: boolean;
  repo?: string;
  updatedAt?: string;
  /** Present when the qa-agent was unreachable / errored (degraded, not fatal). */
  error?: string;
  upstream?: number;
  note?: string;
}

/** Fetch the current open QA issues for a project. The daemon resolves the repo,
 *  injects the qa-agent token, and degrades softly (200 + empty list) on any
 *  upstream problem, so this only throws on a genuine dashboard-auth failure. */
export async function listQaIssues(project: string): Promise<QaIssuesResponse> {
  const res = await fetch(`${DAEMON_URL}/projects/${encodeURIComponent(project)}/qa-issues`, {
    method: "GET",
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* keep status */ }
    throw new Error(msg);
  }
  return (await res.json()) as QaIssuesResponse;
}

/** Developer marks a QA issue fixed ("fix") or undoes an ack ("cancel"). The
 *  daemon resolves the repo, injects the qa-agent token, and records the logged-in
 *  dev's email as the acknowledger. Throws on a dashboard-auth failure or an
 *  upstream-reported failure so the caller can surface it inline; on success the
 *  caller should refresh the panel to pick up the new ack state. */
export async function ackQaIssue(
  project: string,
  signature: string,
  action: "fix" | "cancel",
): Promise<void> {
  const res = await fetch(`${DAEMON_URL}/projects/${encodeURIComponent(project)}/qa-issues/ack`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({ signature, action }),
  });
  if (!res.ok) {
    let msg = `http ${res.status}`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j?.error) msg = j.error;
    } catch { /* keep status */ }
    throw new Error(msg);
  }
  const j = (await res.json()) as { ok?: boolean; reason?: string; error?: string; configured?: boolean };
  if (!j.ok) throw new Error(j.error ?? j.reason ?? (j.configured === false ? "Emil is not connected" : "could not update"));
}
